import React, { useState, useRef, useMemo } from "react";
import Button from "@mui/material/Button";
import MGridForm from "components/Shared/MGridForm";
import validator from "components/Shared/MGridFormDefinitions/validator";
import { DATA_TYPE_DROPDOWN2, DATA_TYPE_STRING, } from "typeConstants/dataTypes";
import { Customer360ValueSettingsDto } from "interfaces/Mueller.Business.Models";

interface Customer360ValueSettingsFormProps {
  customerNumber: number;
  settings: Customer360ValueSettingsDto;
  onSave: (settings: Customer360ValueSettingsDto) => void;
  onCancel: () => void;
  canEdit: boolean;
  portalTargetClass: string;
}

const newSettings: Customer360ValueSettingsDto = {
  customerNumber: null,
  agentGroupId: null,
  agentId: null,
  outputType: "SUMMARY",
  webserviceURL: "https://360-value.com/"
};

const Customer360ValueSettingsForm = (props: Customer360ValueSettingsFormProps) => {
  const [editSettings, setEditSettings] = useState(props.settings ?? newSettings);
  const formControlMethods = useRef({});

  // MGrid form

  const onSettingsChange = updatedObject => setEditSettings({ ...updatedObject });
  const memoizedFormDefinition = useMemo(
    () => {
      const outputTypeOptions = [
        { label: "SUMMARY", value: "SUMMARY"},
        { label: "DETAILED", value: "DETAILED"},
      ];
      const enabled = () => props.canEdit ? true : false;
      const formDef = [
        [{ 
          dataType: DATA_TYPE_STRING, 
          label: "Agent Group Id", 
          source: "agentGroupId", 
          enabled: enabled,
          dataTypeOptions: null,
          hasValidationError: validator.required 
        }],
        [{ 
          dataType: DATA_TYPE_STRING, 
          label: "Agent Id", 
          source: "agentId", 
          enabled: enabled,
          hasValidationError: validator.required 
        }],
      ];
      if (props.canEdit) {
        formDef.push(
          [{ 
            dataType: DATA_TYPE_DROPDOWN2, 
            label: "Output Type", 
            source: "outputType", 
            enabled: enabled,
            dataTypeOptions: { options: outputTypeOptions },
            hasValidationError: validator.required 
          }],
        );
      } else {
        formDef.push(
          [{ 
            dataType: DATA_TYPE_STRING, 
            label: "Output Type", 
            source: "outputType", 
            enabled: enabled,
            hasValidationError: validator.required 
          }],
        );
      }
      formDef.push(
        [{ 
          dataType: DATA_TYPE_STRING, 
          label: "Webservice URL", 
          source: "webserviceURL", 
          enabled: enabled,
          hasValidationError: validator.required 
        }],
      );
      return formDef;
    }, [props.canEdit]);

  // Buttons

  const wrappedOnSave = () => {
    if (formControlMethods.current.isFormValid()) {
      if (!editSettings.customerNumber) {
        // Set this in case we're adding new settings
        editSettings.customerNumber = props.customerNumber;
      }
      props.onSave(editSettings);
    }
  };

  return (
    <div>
      <MGridForm
        width={400}
        data={editSettings}
        onChange={onSettingsChange}
        formDefinition={memoizedFormDefinition}
        portalTarget={() => document.querySelector(props.portalTargetClass)}
        getControlMethods={cm => { formControlMethods.current = cm; }}
      />
        <div className="button-row-margin">
        <Button
          onClick={props.onCancel}
        >{props.canEdit ? "Cancel" : "Close"}</Button>
        {props.canEdit && <Button
          onClick={wrappedOnSave}
        >Save</Button>}
      </div>
    </div>
  );
};

export default Customer360ValueSettingsForm;